/**
 * BulkImportUsers.jsx
 * Route: /admin/users/bulk-import  (add to Admin.jsx routes)
 *
 * Dependencies (add to package.json if not present):
 *   "xlsx": "^0.18.5"
 *
 * Usage in Admin.jsx routes:
 *   import BulkImportUsers from '@/pages/admin/BulkImportUsers';
 *   <Route path="users/bulk-import" element={<BulkImportUsers />} />
 *
 * Also add a link in the Users admin page:
 *   <Link to="/admin/users/bulk-import">Bulk Import</Link>
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Upload, FileSpreadsheet, Users, CheckCircle2, AlertCircle, ChevronLeft, X, Download } from 'lucide-react';
import { bulkImportUsers } from '@/lib/api';

// ── Constants ──────────────────────────────────────────────────────────────
const ROLES = [
  { value: 'requester',  label: 'Requester',  desc: 'Portal only — raises tickets' },
  { value: 'technician', label: 'Technician', desc: 'Admin panel — handles tickets' },
  { value: 'authority',  label: 'Authority',  desc: 'Category head — oversees a category' },
  { value: 'admin',      label: 'Admin',       desc: 'Full admin access' },
];

const ROLE_COLORS = {
  requester:  'bg-gray-100 text-gray-700',
  technician: 'bg-green-100 text-green-700',
  authority:  'bg-amber-100 text-amber-700',
  admin:      'bg-blue-100 text-blue-700',
};

// Column name variants the auto-detector recognises (case-insensitive)
const COLUMN_ALIASES = {
  name:        ['name', 'full name', 'name of the faculty', 'employee name', 'staff name'],
  email:       ['email', 'email id', 'e-mail', 'e-mail id', 'mail'],
  phone:       ['phone', 'contact', 'contact no', 'contact no.', 'mobile', 'phone no'],
  department:  ['department', 'dept', 'dept.'],
  designation: ['designation', 'designation/post', 'post'],
};

// ── Parser ─────────────────────────────────────────────────────────────────
function detectColumnIndex(headers) {
  const lower = headers.map(h => (h ?? '').toString().toLowerCase().trim());
  const result = {};
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    for (let i = 0; i < lower.length; i++) {
      if (aliases.some(alias => lower[i].includes(alias))) {
        result[field] = i;
        break;
      }
    }
  }
  return result;
}

function parseSheet(ws) {
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (!rawRows.length) return [];

  // Find the header row: first row where ≥2 cells look like column labels (non-numeric strings)
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(rawRows.length, 6); i++) {
    const row = rawRows[i];
    const stringCells = row.filter(c => typeof c === 'string' && c.length > 1 && isNaN(c));
    if (stringCells.length >= 2) { headerRowIdx = i; break; }
  }
  if (headerRowIdx < 0) return [];

  const colMap = detectColumnIndex(rawRows[headerRowIdx]);
  if (!colMap.name && !colMap.email) return []; // can't find essential columns

  const users = [];
  for (let i = headerRowIdx + 2; i < rawRows.length; i++) { // +2 skips potential sub-header
    const row = rawRows[i];
    if (!row || row.every(c => c === null)) continue;

    const name  = colMap.name  != null ? (row[colMap.name]  ?? '').toString().trim() : '';
    const email = colMap.email != null ? (row[colMap.email] ?? '').toString().trim() : '';

    // Skip formula rows, blank rows, or rows without a real name
    if (!name || name.startsWith('=') || !email || !email.includes('@')) continue;

    // Clean phone: take first number from possibly messy strings like "9620 / 8123"
    let phone = '';
    if (colMap.phone != null && row[colMap.phone] != null) {
      const raw = String(row[colMap.phone]).trim();
      if (raw !== '-') {
        const digits = raw.replace(/[^0-9]/g, '').slice(0, 15);
        if (digits.length >= 7) phone = digits;
      }
    }

    users.push({
      name,
      email: email.toLowerCase(),
      phone,
      department:  colMap.department  != null ? (row[colMap.department]  ?? '').toString().trim().replace(/\n.*/s, '') : '',
      designation: colMap.designation != null ? (row[colMap.designation] ?? '').toString().trim().replace(/\n.*/s, '') : '',
      role: 'technician', // default — user overrides per-row or via base role
      _key: `${email}-${i}`,
    });
  }
  return users;
}

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array', cellDates: true });
        let allUsers = [];
        for (const sheetName of wb.SheetNames) {
          const parsed = parseSheet(wb.Sheets[sheetName]);
          allUsers = allUsers.concat(parsed.map(u => ({ ...u, _sheet: sheetName })));
        }
        // Deduplicate by email
        const seen = new Set();
        allUsers = allUsers.filter(u => {
          if (seen.has(u.email)) return false;
          seen.add(u.email);
          return true;
        });
        resolve(allUsers);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// ── CSV Template download ──────────────────────────────────────────────────
function downloadTemplate() {
  const csv = [
    'name,email,phone,department,designation',
    'Dr. Anupama H S,anupamahs@bmsit.in,9741043410,AIML,Professor & HOD',
    'Mr. H N Prakash Kumar,hnprakashkumar@bmsit.in,9902884437,ECE,Foreman',
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'bulk-import-template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ── Main component ─────────────────────────────────────────────────────────
export default function BulkImportUsers() {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [stage, setStage]           = useState('idle');    // idle | preview | importing | done
  const [dragging, setDragging]     = useState(false);
  const [parseError, setParseError] = useState('');
  const [rows, setRows]             = useState([]);        // parsed user rows
  const [baseRole, setBaseRole]     = useState('technician');
  const [sendWelcome, setSendWelcome] = useState(true);
  const [result, setResult]         = useState(null);      // { created, skipped, errors }
  const [importing, setImporting]   = useState(false);

  // ── Apply base role to all rows that haven't been individually overridden ──
  const applyBaseRole = useCallback((newBase) => {
    setBaseRole(newBase);
    setRows(prev => prev.map(r => ({ ...r, role: newBase })));
  }, []);

  const setRowRole = (key, role) => {
    setRows(prev => prev.map(r => r._key === key ? { ...r, role } : r));
  };

  const removeRow = (key) => {
    setRows(prev => prev.filter(r => r._key !== key));
  };

  // ── File handling ──────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setParseError('Please upload an .xlsx, .xls or .csv file.');
      return;
    }
    setParseError('');
    try {
      const parsed = await parseFile(file);
      if (!parsed.length) {
        setParseError('No valid users found. Make sure the file has Name and Email columns.');
        return;
      }
      // Apply current base role
      setRows(parsed.map(r => ({ ...r, role: baseRole })));
      setStage('preview');
    } catch (err) {
      setParseError(`Could not parse file: ${err.message}`);
    }
  };

  const onFileChange = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  // ── Import ─────────────────────────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    try {
      const payload = rows.map(({ name, email, phone, department, designation, role }) => ({
        name, email, phone, department, designation, role,
      }));
      const res = await bulkImportUsers({ users: payload, role: baseRole, send_welcome: sendWelcome });
      setResult(res);
      setStage('done');
    } catch (err) {
      setResult({ created: 0, skipped: 0, errors: [{ email: '—', reason: err.message }] });
      setStage('done');
    } finally {
      setImporting(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────
  const validRows  = rows.filter(r => r.email && r.name);
  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground p-1">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Bulk Import Users</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Upload an Excel or CSV file to add staff to this institute in one go.
          </p>
        </div>
      </div>

      {/* ── STAGE: idle ── */}
      {stage === 'idle' && (
        <div className="space-y-5">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-14 text-center cursor-pointer transition-colors
              ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
          >
            <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium text-sm">Drop your Excel or CSV here</p>
            <p className="text-xs text-muted-foreground mt-1">or click to browse &nbsp;·&nbsp; .xlsx &nbsp;.xls &nbsp;.csv</p>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFileChange} />
          </div>

          {parseError && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {parseError}
            </div>
          )}

          {/* Template download */}
          <div className="glass rounded-xl p-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Need a template?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Download a CSV with the correct columns: name, email, phone, department, designation.
              </p>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 text-sm text-primary hover:underline whitespace-nowrap"
            >
              <Download className="h-4 w-4" />
              Download template
            </button>
          </div>

          {/* Notes */}
          <div className="text-xs text-muted-foreground space-y-1 px-1">
            <p>• Multi-sheet Excel files are supported — all sheets are combined and deduplicated by email.</p>
            <p>• Existing users (matched by email) are skipped — their data is never overwritten.</p>
            <p>• Imported users log in via OTP on first access (no password required initially).</p>
          </div>
        </div>
      )}

      {/* ── STAGE: preview ── */}
      {stage === 'preview' && (
        <div className="space-y-5">
          {/* Summary bar */}
          <div className="glass rounded-xl p-5 flex flex-wrap gap-6 items-center">
            <div className="flex items-center gap-2.5">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold font-display">{rows.length}</span>
              <span className="text-sm text-muted-foreground">users found</span>
            </div>
            <div className="h-8 w-px bg-border" />

            {/* Base role picker */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium">Import all as:</span>
              {ROLES.map(r => (
                <button
                  key={r.value}
                  onClick={() => applyBaseRole(r.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all
                    ${baseRole === r.value
                      ? 'border-primary bg-primary text-white'
                      : 'border-border hover:border-primary/50'}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="h-8 w-px bg-border" />

            {/* Welcome email toggle */}
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={sendWelcome}
                onChange={e => setSendWelcome(e.target.checked)}
                className="rounded"
              />
              Send welcome email
            </label>

            {/* Reset */}
            <button
              onClick={() => { setStage('idle'); setRows([]); }}
              className="ml-auto text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X className="h-4 w-4" /> Change file
            </button>
          </div>

          {/* Role legend */}
          <p className="text-xs text-muted-foreground px-1">
            You can override the role per row using the dropdown in each row. The base role above applies to all rows at once.
          </p>

          {/* Table */}
          <div className="rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="px-4 py-3 font-medium text-xs text-muted-foreground uppercase tracking-wide">Name</th>
                    <th className="px-4 py-3 font-medium text-xs text-muted-foreground uppercase tracking-wide">Email</th>
                    <th className="px-4 py-3 font-medium text-xs text-muted-foreground uppercase tracking-wide">Phone</th>
                    <th className="px-4 py-3 font-medium text-xs text-muted-foreground uppercase tracking-wide">Department</th>
                    <th className="px-4 py-3 font-medium text-xs text-muted-foreground uppercase tracking-wide">Designation</th>
                    <th className="px-4 py-3 font-medium text-xs text-muted-foreground uppercase tracking-wide">Role</th>
                    <th className="px-2 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map(row => {
                    const valid = emailValid(row.email) && row.name;
                    return (
                      <tr key={row._key} className={`hover:bg-muted/20 ${!valid ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-2.5 font-medium">{row.name || <span className="text-red-500">—</span>}</td>
                        <td className="px-4 py-2.5 text-muted-foreground font-mono text-xs">
                          {emailValid(row.email)
                            ? row.email
                            : <span className="text-red-500">{row.email || '—'}</span>}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{row.phone || '—'}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{row.department || '—'}</td>
                        <td className="px-4 py-2.5 text-muted-foreground text-xs max-w-[160px] truncate" title={row.designation}>
                          {row.designation || '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          <select
                            value={row.role}
                            onChange={e => setRowRole(row._key, e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer
                              focus:ring-1 focus:ring-primary outline-none ${ROLE_COLORS[row.role]}`}
                          >
                            {ROLES.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2.5">
                          <button
                            onClick={() => removeRow(row._key)}
                            className="text-muted-foreground/40 hover:text-red-500 transition-colors p-1"
                            title="Remove this row"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Import button */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground">
              {validRows.length} valid &nbsp;·&nbsp; {rows.length - validRows.length} invalid (will be skipped)
            </p>
            <button
              onClick={handleImport}
              disabled={importing || validRows.length === 0}
              className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl
                font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Upload className="h-4 w-4" />
              {importing ? 'Importing…' : `Import ${validRows.length} users`}
            </button>
          </div>
        </div>
      )}

      {/* ── STAGE: done ── */}
      {stage === 'done' && result && (
        <div className="space-y-5">
          {/* Result cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="glass rounded-2xl p-6 text-center">
              <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-3xl font-bold font-display text-green-600">{result.created}</p>
              <p className="text-sm text-muted-foreground mt-1">Created</p>
            </div>
            <div className="glass rounded-2xl p-6 text-center">
              <p className="text-3xl font-bold font-display text-amber-500">{result.skipped}</p>
              <p className="text-sm text-muted-foreground mt-1">Skipped (already exist)</p>
            </div>
            <div className="glass rounded-2xl p-6 text-center">
              <p className="text-3xl font-bold font-display text-red-500">{result.errors?.length ?? 0}</p>
              <p className="text-sm text-muted-foreground mt-1">Errors</p>
            </div>
          </div>

          {/* Errors detail */}
          {result.errors?.length > 0 && (
            <div className="rounded-xl border border-red-200 overflow-hidden">
              <div className="bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700">
                Failed rows
              </div>
              <div className="divide-y divide-border">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-4 py-2 text-sm flex gap-4">
                    <span className="font-mono text-xs text-muted-foreground">{e.email}</span>
                    <span className="text-red-600">{e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next steps */}
          <div className="glass rounded-xl p-5 space-y-2">
            <p className="text-sm font-medium">What's next</p>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">→</span>
                Go to <strong>Categories</strong> and set a default assignee — the imported users are now searchable by name or department.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">→</span>
                Set up <strong>Escalation Chains</strong> — add L2, L3 contacts per category from the same user pool.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">→</span>
                Users log in via <strong>OTP</strong> at first access — no password needed.
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => { setStage('idle'); setRows([]); setResult(null); }}
              className="px-5 py-2.5 rounded-xl border text-sm font-medium hover:bg-muted/30 transition-colors"
            >
              Import another file
            </button>
            <button
              onClick={() => navigate('/admin/users')}
              className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              View all users
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
